/**
 * Removes all `undefined`, `null`, and empty objects from a nested structure.
 *
 * This utility recursively walks arrays and plain objects, cleaning out values
 * that should not be sent to the backend or stored. It ensures that the final
 * object contains only meaningful data, preventing issues with serialization,
 * validation, and deep comparison logic.
 *
 * @param obj - The input value (object, array, or primitive) to clean.
 * @returns The cleaned value with empty/undefined structures removed, or
 *          `undefined` if nothing meaningful remains.
 */
export function removeUndefinedAndEmpty(obj: unknown): unknown {
    // Handle arrays
    if (Array.isArray(obj)) {
        const arr = obj
            .map((item) => removeUndefinedAndEmpty(item))
            .filter((item): item is NonNullable<typeof item> => item !== undefined && item !== null);

        return arr.length > 0 ? arr : undefined;
    }

    // Handle plain objects only
    if (obj !== null && typeof obj === 'object') {
        const inputObj = obj as Record<string, unknown>;
        const cleaned: Record<string, unknown> = {};

        const keys = Object.keys(inputObj);

        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const value = removeUndefinedAndEmpty(inputObj[key]);

            if (value !== undefined && value !== null) {
                cleaned[key] = value;
            }
        }

        return Object.keys(cleaned).length > 0 ? cleaned : undefined;
    }

    // Primitive values (string, number, boolean, etc.)
    return obj;
}
